// services/notificationService.ts
import { BACKEND_URL } from "../utils/api";

class NotificationService {
  private token: string | null = null;

  setToken(token: string) {
    this.token = token;
  }

  private async request(endpoint: string, options: RequestInit = {}) {
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    if (typeof options.body === "string" && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${BACKEND_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.detail || "Erreur serveur");
    }

    return data;
  }

  // --- NOTIFICATIONS IN-APP ---
  async sendNotification(payload: {
    title: string;
    message: string;
    type: "TRANSACTION" | "SYSTEM" | "ADMIN" | "KYC" | "PROMOTION" | "SECURITY";
    priority: "LOW" | "NORMAL" | "HIGH" | "URGENT";
    userId?: string;
    data?: string;
    actionUrl?: string;
    actionLabel?: string;
  }) {
    const isBroadcast = !payload.userId;
    const endpoint = isBroadcast ? "/notifications/admin/broadcast" : "/notifications/admin/create";

    const body: any = {
      title: payload.title,
      message: payload.message,
      type: payload.type,
      priority: payload.priority,
    };

    if (payload.userId) body.user_id = payload.userId;
    if (payload.data) body.data = payload.data;
    if (payload.actionUrl) body.action_url = payload.actionUrl;
    if (payload.actionLabel) body.action_label = payload.actionLabel;

    return this.request(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  // --- PUSH NOTIFICATIONS ---
  async sendPushNotification(payload: {
    title: string;
    description: string;
    userIds?: string[];
    broadcast?: boolean;
    image?: string;
  }) {
    const body: any = {
      title: payload.title,
      description: payload.description,
    };

    if (payload.broadcast) {
      body.broadcast = true;
    } else if (payload.userIds) {
      body.user_ids = payload.userIds;
    }

    if (payload.image) body.image = payload.image;

    return this.request("/notifications/admin/push", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  // --- EMAIL SANS TEMPLATE ---
  async sendEmail(payload: {
    subject: string;
    message: string;
    userIds?: string[];
    broadcast?: boolean;
    attachments?: File[];
  }) {
    const formData = new FormData();
    formData.append("subject", payload.subject);
    formData.append("message", payload.message);

    if (payload.broadcast) {
      formData.append("broadcast", "true");
    } else if (payload.userIds) {
      payload.userIds.forEach((id) => formData.append("user_ids", id));
    }

    if (payload.attachments) {
      payload.attachments.forEach((file) => formData.append("attachments", file));
    }

    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${BACKEND_URL}/emails/admin/send`, {
      method: "POST",
      headers,
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.detail || "Erreur lors de l'envoi");
    }

    return data;
  }

  // --- EMAIL AVEC TEMPLATE (placeholders) ---
  async sendTemplateEmail(payload: {
    subject: string;
    html: string;
    userIds?: string[];
    broadcast?: boolean;
    attachments?: File[];
  }) {
    const formData = new FormData();
    formData.append("subject", payload.subject);
    formData.append("html", payload.html);

    if (payload.broadcast) {
      formData.append("broadcast", "true");
    } else if (payload.userIds) {
      payload.userIds.forEach((id) => formData.append("user_ids", id));
    }

    if (payload.attachments) {
      payload.attachments.forEach((file) => formData.append("attachments", file));
    }

    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${BACKEND_URL}/emails/admin/send-template`, {
      method: "POST",
      headers,
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.detail || "Erreur lors de l'envoi");
    }

    return data;
  }

  // --- GESTION DES DESINSCRITS ---
  async getUnsubscribedEmails() {
    return this.request("/emails/unsubscribed", {
      method: "GET",
    });
  }

  async addUnsubscribedEmail(email: string, userId?: string) {
    const body: any = { email };
    if (userId) body.user_id = userId;
    return this.request("/emails/unsubscribed", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async removeUnsubscribedEmail(unsubscribeId: string) {
    return this.request(`/emails/unsubscribed/${unsubscribeId}`, {
      method: "DELETE",
    });
  }

  // --- GESTION DES DESINSCRITS NOTIFICATIONS ---
  async getUnsubscribedNotifications() {
    return this.request("/notifications/unsubscribed", {
      method: "GET",
    });
  }

  async addUnsubscribedNotification(email: string, userId?: string) {
    const body: any = { email };
    if (userId) body.user_id = userId;
    return this.request("/notifications/unsubscribed", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async removeUnsubscribedNotification(unsubscribeId: string) {
    return this.request(`/notifications/unsubscribed/${unsubscribeId}`, {
      method: "DELETE",
    });
  }

  // --- DESINSCRIPTION PUBLIQUE (depuis le lien email) ---
  async publicUnsubscribe(email: string, userId?: string) {
    const body: any = { email };
    if (userId) body.user_id = userId;
    const response = await fetch(`${BACKEND_URL}/emails/unsubscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || data.detail || "Erreur lors de la désinscription");
    }
    return data;
  }
}

export const notificationService = new NotificationService();
